"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import { useSeededJsonTranslations } from "@/contexts/JsonTranslationsContext";
import { useLanguageSafe } from "@/contexts/LanguageContext";
import { APP_VERSION } from "@/lib/constants/version";
import { reportWarning } from "@/lib/observability/report-error";

// Cache for loaded translation files
const translationCache = new Map<string, Record<string, unknown>>();

// Cache for loading promises to prevent duplicate requests
const loadingPromises = new Map<string, Promise<Record<string, unknown>>>();

// Track failed fetches so useEffect can retry (keys that have permanently failed)
const failedKeys = new Map<string, number>();
const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 3000, 8000]; // exponential backoff
const shouldCacheTranslations =
  process.env.NODE_ENV === "production" &&
  !(
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname)
  );

/**
 * Load translation file for a specific language and namespace
 */
async function loadTranslationFile(
  language: string,
  namespace: string
): Promise<Record<string, unknown>> {
  const cacheKey = `${language}:${namespace}`;

  // Check in-memory cache first (always active to prevent duplicate fetches)
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey)!;
  }

  // Check if already loading
  if (loadingPromises.has(cacheKey)) {
    return loadingPromises.get(cacheKey)!;
  }

  // Start loading with retry for transient network failures (NODE-1TT)
  const loadPromise = (async () => {
    const attempt = failedKeys.get(cacheKey) ?? 0;
    try {
      // Bust browser/CDN caches on each deploy — stale JSON + new JS causes false
      // "missing key" warnings when keys were added after the cached file was saved.
      const url = `/locales/${language}/${namespace}.json?v=${encodeURIComponent(APP_VERSION)}`;
      const response = await fetch(url, {
        cache: shouldCacheTranslations ? "default" : "no-store",
      });

      if (!response.ok) {
        // Retry transient server errors (502/503/504 during deploys, CDN blips)
        // before falling back — static JSON files should always be available.
        if (response.status >= 500 && attempt < MAX_RETRIES) {
          failedKeys.set(cacheKey, attempt + 1);
          loadingPromises.delete(cacheKey);
          const delay =
            RETRY_DELAYS[attempt] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1];
          await new Promise((r) => setTimeout(r, delay));
          return loadTranslationFile(language, namespace);
        }

        // If file doesn't exist, try English fallback
        if (language !== "en") {
          console.warn(
            `Translation file not found: /locales/${language}/${namespace}.json, falling back to English`
          );
          const englishFallback = await loadTranslationFile("en", namespace);
          translationCache.set(`en:${namespace}`, englishFallback);
          return englishFallback;
        }
        throw new Error(
          `Translation file not found: /locales/${language}/${namespace}.json (HTTP ${response.status})`
        );
      }

      const data = await response.json();
      translationCache.set(cacheKey, data);
      failedKeys.delete(cacheKey);
      return data;
    } catch (error) {
      // Retry transient network failures (TypeError: Failed to fetch / Load failed)
      const isNetworkError =
        error instanceof TypeError &&
        (error.message === "Failed to fetch" ||
          error.message === "Load failed");
      if (isNetworkError && attempt < MAX_RETRIES) {
        failedKeys.set(cacheKey, attempt + 1);
        loadingPromises.delete(cacheKey);
        const delay =
          RETRY_DELAYS[attempt] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1];
        await new Promise((r) => setTimeout(r, delay));
        return loadTranslationFile(language, namespace);
      }

      reportWarning(error, {
        source: "hooks.useJsonTranslation",
        feature: "i18n",
        action: "loadTranslationFile",
        extra: { language, namespace, attempt },
      });
      // REGRESSION GUARD — NODE-23F / NODE-238 (2026-04-06):
      // Re-throw so Promise.all rejects and the caller's .catch() keeps
      // isLoading=true. Returning {} here causes t() to walk the empty
      // object, find undefined, and fire false "missing key" warnings
      // for keys that actually exist in the locale files.
      throw error;
    } finally {
      loadingPromises.delete(cacheKey);
    }
  })();

  loadingPromises.set(cacheKey, loadPromise);
  return loadPromise;
}

function getCachedTranslations(language: string, namespace: string) {
  return translationCache.get(`${language}:${namespace}`) ?? null;
}

/**
 * Hook for loading JSON-based translations per component
 *
 * @param namespace - The translation namespace (component name)
 * @returns Translation function and loading state, plus language context properties
 *
 * @example
 * ```tsx
 * const { t, language, isRTL, direction } = useJsonTranslation("MyComponent");
 * return <div>{t("title")}</div>;
 * ```
 */
export function useJsonTranslation(namespace: string) {
  const languageContext = useLanguageSafe();
  const language = languageContext?.language ?? "en";
  const seededTranslations = useSeededJsonTranslations();
  const seededNamespace = seededTranslations[namespace];
  const seededMatchesLanguage = seededNamespace?.language === language;
  const activeCacheKey = `${language}:${namespace}`;
  const cachedCurrent = getCachedTranslations(language, namespace);
  const cachedEnglish =
    language === "en" ? cachedCurrent : getCachedTranslations("en", namespace);
  const [loadedTranslations, setLoadedTranslations] = useState<{
    cacheKey: string | null;
    current: Record<string, unknown>;
    english: Record<string, unknown>;
  }>({
    cacheKey: null,
    current: {},
    english: {},
  });

  // Load translations when language or namespace changes
  useEffect(() => {
    let isMounted = true;

    if (seededNamespace && seededMatchesLanguage) {
      translationCache.set(`${language}:${namespace}`, seededNamespace.current);
      translationCache.set(`en:${namespace}`, seededNamespace.english);

      return () => {
        isMounted = false;
      };
    }

    if (cachedCurrent) {
      return () => {
        isMounted = false;
      };
    }

    // Always load English as fallback
    const englishPromise =
      language !== "en"
        ? loadTranslationFile("en", namespace)
        : Promise.resolve({});
    const currentPromise = loadTranslationFile(language, namespace);

    Promise.all([currentPromise, englishPromise])
      .then(([data, enData]) => {
        if (isMounted) {
          setLoadedTranslations({
            cacheKey: activeCacheKey,
            current: data,
            english: enData,
          });
        }
      })
      .catch((error) => {
        reportWarning(error, {
          source: "hooks.useJsonTranslation",
          feature: "i18n",
          action: "loadTranslations",
          extra: { language, namespace },
        });
        // REGRESSION GUARD — NODE-238 (missing translation key: comparison.badge)
        // Do NOT set cacheKey to activeCacheKey on failure — that makes
        // isLoading=false while translations are empty {}, causing t() to
        // report "missing key" warnings for keys that actually exist.
        // Leaving cacheKey as null keeps isLoading=true, so t() returns ""
        // (blank) instead of firing false "missing key" reports. The next
        // render will retry the fetch via the useEffect dependency.
      });

    return () => {
      isMounted = false;
    };
  }, [
    activeCacheKey,
    cachedCurrent,
    language,
    namespace,
    seededMatchesLanguage,
    seededNamespace,
  ]);

  const translations = useMemo(
    () =>
      seededMatchesLanguage
        ? seededNamespace.current
        : loadedTranslations.cacheKey === activeCacheKey
          ? loadedTranslations.current
          : (cachedCurrent ?? {}),
    [
      activeCacheKey,
      cachedCurrent,
      loadedTranslations,
      seededMatchesLanguage,
      seededNamespace,
    ]
  );
  const englishTranslations = useMemo(
    () =>
      seededMatchesLanguage
        ? seededNamespace.english
        : loadedTranslations.cacheKey === activeCacheKey
          ? loadedTranslations.english
          : (cachedEnglish ?? {}),
    [
      activeCacheKey,
      cachedEnglish,
      loadedTranslations,
      seededMatchesLanguage,
      seededNamespace,
    ]
  );
  const isLoading =
    !seededMatchesLanguage &&
    !cachedCurrent &&
    loadedTranslations.cacheKey !== activeCacheKey;

  // Helper to get nested value from translations
  const getNestedValue = useCallback(
    (obj: Record<string, unknown>, keyPath: string[]): unknown => {
      let value: unknown = obj;
      for (const k of keyPath) {
        if (value && typeof value === "object" && k in value) {
          value = (value as Record<string, unknown>)[k];
        } else {
          return undefined;
        }
      }
      return value;
    },
    []
  );

  // Translation function
  const t = useCallback(
    (key: string, params?: Record<string, unknown>): string => {
      if (isLoading) {
        // SENTRY FIX: NODE-2GG / NODE-2GH — support defaultValue while loading
        // Date: 2026-04-15 | Agent: Claude Code
        // Root cause: Returning "" ignores the caller's defaultValue param.
        // Fix: honour defaultValue if provided, otherwise blank to avoid flashing.
        const dv = params?.defaultValue;
        return typeof dv === "string" ? dv : "";
      }

      // SENTRY FIX: NODE-2GG / NODE-2GH — Missing translation key: toolCall.get_my_projects
      // Date: 2026-04-15 | Agent: Claude Code
      // Root cause: Flat keys with dots (e.g. "toolCall.get_my_projects") in locale
      //   JSON are split on "." and navigated as nested paths. The English Copilot.json
      //   stores these as flat top-level keys, so nested navigation returns undefined.
      // Fix: Try the exact (flat) key first, then fall back to dot-split (nested) lookup.
      // DO NOT: Remove the flat-key check — locale files use both flat and nested formats.

      // 1. Try exact (flat) key in current language
      let value: unknown =
        key in translations
          ? (translations as Record<string, unknown>)[key]
          : undefined;

      // 2. If flat lookup missed, try nested dot-split path
      if (value === undefined && key.includes(".")) {
        const keys = key.split(".");
        value = getNestedValue(translations, keys);
      }

      // 3. Fall back to English (flat then nested)
      if (value === undefined && language !== "en") {
        value =
          key in englishTranslations
            ? (englishTranslations as Record<string, unknown>)[key]
            : undefined;
        if (value === undefined && key.includes(".")) {
          value = getNestedValue(englishTranslations, key.split("."));
        }
      }

      // If still not found, use defaultValue if provided, else report + return key.
      // Skip reporting when translations object is empty — this means the
      // fetch hasn't populated state yet (race between isLoading flip and
      // translations object update). Reporting here creates false "missing
      // key" warnings for keys that exist in the locale files.
      if (value === undefined) {
        // Honour caller-provided defaultValue (e.g. CopilotMessage uses it)
        const dv = params?.defaultValue;
        if (typeof dv === "string") return dv;

        const translationsLoaded = Object.keys(translations).length > 0;
        if (translationsLoaded) {
          reportWarning(
            new Error(
              `Missing translation key: ${key} in namespace: ${namespace}`
            ),
            {
              source: "hooks.useJsonTranslation",
              feature: "i18n",
              action: "missingKey",
              extra: { key, namespace, language },
            }
          );
        }
        return key;
      }

      // SENTRY FIX: VIDSEEDS-2MB, VIDSEEDS-2MC, VIDSEEDS-2M2, VIDSEEDS-2M9 — adAnchors parent object
      // Date: 2026-04-21 | Agent: Claude Code
      // Root cause: Nested translation objects (e.g. report.adAnchors.voiceQuality) or arrays
      //   (e.g. bullets, features, highlights lists) are valid structures. Only leaf keys
      //   should resolve to strings; intermediate nodes and lists should not trigger warnings.
      // Fix: Report ONLY for invalid primitives (null, number, boolean). Arrays and objects
      //   are explicitly allowed.
      if (typeof value !== "string") {
        const isValidStructure = value !== null && typeof value === "object"; // objects (nested) or arrays (lists)
        if (!isValidStructure) {
          reportWarning(
            new Error(
              `Translation value is not a string for key: ${key} in namespace: ${namespace}`
            ),
            {
              source: "hooks.useJsonTranslation",
              feature: "i18n",
              action: "nonStringValue",
              extra: { key, namespace, language },
            }
          );
        }
        // Honor caller-provided defaultValue for consistency with missing-key and loading paths
        const dv = params?.defaultValue;
        if (typeof dv === "string") return dv;
        return key;
      }

      // Replace parameters if provided
      if (params) {
        let result = value;
        for (const [paramKey, paramValue] of Object.entries(params)) {
          result = result.replace(
            new RegExp(`{{${paramKey}}}`, "g"),
            String(paramValue)
          );
        }
        return result;
      }

      return value;
    },
    [
      translations,
      englishTranslations,
      isLoading,
      namespace,
      language,
      getNestedValue,
    ]
  );

  // Rich text function for HTML content
  const rich = useCallback(
    (key: string, params?: Record<string, unknown>) => {
      const text = t(key, params);
      // For rich text, we could add HTML parsing here if needed
      // For now, just return the text (components can handle HTML)
      return text;
    },
    [t]
  );

  return {
    t,
    rich,
    isLoading,
    hasTranslations: Object.keys(translations).length > 0,
    // Include all language context properties for full compatibility
    language,
    isRTL: languageContext?.isRTL ?? false,
    direction: languageContext?.direction ?? ("ltr" as const),
    isChanging: languageContext?.isChanging ?? false,
    setLanguage: languageContext?.setLanguage ?? (async () => {}),
    getName: languageContext?.getName ?? ((code?: string) => code ?? language),
    getNativeName:
      languageContext?.getNativeName ?? ((code?: string) => code ?? language),
    getFlag: languageContext?.getFlag ?? (() => "🌐"),
    getMetadata: languageContext?.getMetadata ?? (() => undefined),
    availableLanguages: languageContext?.availableLanguages ?? [],
  };
}

/**
 * Preload translations for better performance
 */
export function preloadTranslations(language: string, namespaces: string[]) {
  return Promise.all(
    namespaces.map((namespace) => loadTranslationFile(language, namespace))
  );
}

/**
 * Clear translation cache (useful for development)
 */
export function clearTranslationCache() {
  translationCache.clear();
  loadingPromises.clear();
}
